import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { FeatureIcon } from './featureIcons';
import { resolveFeatureImage } from '../../utils/homeFeatureImage';
import type { HomeFeatureItem } from '../../types/homeFeatures';

interface FeatureCardProps {
  item: HomeFeatureItem;
  /** featured: スポットライト行（大・左右交互） / compact: グラスタイル */
  variant: 'featured' | 'compact';
  /** featured で画像を右側に置く（交互レイアウト用） */
  imageRight?: boolean;
}

// グラスのアイコンチップ：フロスト下地 + ライムのアイコン（黄色は「面」でなく「光」）
const IconChip: React.FC<{ name?: string; size?: number }> = ({ name, size = 44 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: `${Math.round(size * 0.32)}px`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}26, rgba(255,255,255,0.04))`,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: (theme) => `1px solid ${theme.palette.primary.main}3D`,
      boxShadow: (theme) => `0 4px 16px ${theme.palette.primary.main}1F, inset 0 1px 0 rgba(255,255,255,0.14)`,
    }}
  >
    <FeatureIcon name={name} sx={{ fontSize: size * 0.5, color: 'primary.main' }} />
  </Box>
);

// 要点：本物のチェックアイコン（ライム）、塗らないインライン列
const PointList: React.FC<{ points?: string[]; dense?: boolean }> = ({ points, dense }) => {
  if (!points || points.length === 0) return null;
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mt: dense ? 1.5 : 2 }}>
      {points.map((point, i) => (
        <Box
          component="li"
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: i < points.length - 1 ? 1 : 0,
            color: 'text.secondary',
            fontSize: dense ? { xs: '0.7rem', md: '0.8rem' } : { xs: '0.75rem', md: '0.9rem' },
            lineHeight: 1.25,
          }}
        >
          <CheckRoundedIcon sx={{ mt: '1px', fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
          {point}
        </Box>
      ))}
    </Box>
  );
};

// 画像（なければアイコンを敷いた極薄プレースホルダ）
const FeatureImage: React.FC<{ src?: string; alt: string; iconName?: string }> = ({ src, alt, iconName }) => {
  if (src) {
    return (
      <Box component="img" src={src} alt={alt} sx={{ width: '100%', display: 'block', objectFit: 'cover', borderRadius: 'inherit' }} />
    );
  }
  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '16/10',
        borderRadius: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}14, rgba(255,255,255,0.02))`,
      }}
    >
      <FeatureIcon name={iconName} sx={{ fontSize: 56, color: 'primary.main', opacity: 0.4 }} />
    </Box>
  );
};

// 共通のグラスサーフェス
const glassSurface = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
};

const FeatureCard: React.FC<FeatureCardProps> = ({ item, variant, imageRight }) => {
  const interactive = !!item.link;
  const rootLink = item.link ? { component: RouterLink, to: item.link } : {};
  const img = resolveFeatureImage(item.image);

  if (variant === 'compact') {
    return (
      <Box
        {...(rootLink as any)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '20px',
          overflow: 'hidden',
          textDecoration: 'none',
          color: 'inherit',
          transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
          ...glassSurface,
          ...(interactive && {
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-4px)',
              borderColor: (theme: any) => `${theme.palette.primary.main}66`,
              boxShadow: (theme: any) => `0 16px 40px rgba(0,0,0,0.3), 0 0 0 1px ${theme.palette.primary.main}22, inset 0 1px 0 rgba(255,255,255,0.08)`,
            },
          }),
        }}
      >
        {img && (
          <Box sx={{ overflow: 'hidden' }}>
            <FeatureImage src={img} alt={item.title} iconName={item.icon} />
          </Box>
        )}
        <Box sx={{ p: { xs: 2.5, md: 3 }, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <IconChip name={item.icon} size={36} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              {item.title}
            </Typography>
          </Box>
          <Typography sx={{ color: 'text.secondary', fontSize: { xs: '0.82rem', md: '0.9rem' }, lineHeight: 1.5 }}>
            {item.description}
          </Typography>
          <PointList points={item.points} dense />
        </Box>
      </Box>
    );
  }

  // featured: スポットライト行
  return (
    <Box
      {...(rootLink as any)}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: imageRight ? 'row' : 'row-reverse' },
        alignItems: 'center',
        gap: { xs: 2.5, md: 5 },
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {/* テキスト */}
      <Box sx={{ flex: 1, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 1.75 }}>
          <IconChip name={item.icon} size={40} />
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.5rem' }, letterSpacing: '-0.01em' }}>
            {item.title}
          </Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary', fontSize: { xs: '0.85rem', md: '0.95rem' }, lineHeight: 1.5, maxWidth: 480 }}>
          {item.description}
        </Typography>
        <PointList points={item.points} />
      </Box>

      {/* 画像（枠線なし・落ち影のみ） */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
        }}
      >
        <FeatureImage src={img} alt={item.title} iconName={item.icon} />
      </Box>
    </Box>
  );
};

export default FeatureCard;
